import { useQuery } from '@tanstack/react-query';
import type { FundingAdminListItem, FundingStatusEnum } from '@/lib/api-admin/types';
import { apiClient } from '@/lib/apiClient';
import {
  FUNDING_STATUS_VALUES,
  type FundingListFilter,
  type FundingListSort,
  type FundingRow,
} from '../lib/fundingListView';

/**
 * QA-398 — 펀딩 캠페인 목록(서버 검색·정렬). settlement #9 가 목록에 구분·참여 수·리워드 수를, 검색 조건(구분·펀딩 기간·
 * 런칭일·마감일)과 정렬(달성률·런칭일·마감일)을 넣어 서버에서 처리한다.
 * - 페이지는 1부터(BE PageRequest.of(page - 1, …)).
 * - 날짜 조건은 KST 하루 경계(00:00:00.000 ~ 23:59:59.999)의 ISO Instant, 양끝 포함.
 * - 공개 여부는 별도 파라미터가 없어 상태로 보낸다: 비공개 = DRAFT, 공개 = DRAFT 외. 고른 상태와 겹치는 것만.
 */
export const PAGE_SIZE = 20;

type ListItem = FundingAdminListItem & { category?: string | null; currentPledgeCount?: number; rewardCount?: number };

const KST = '+09:00';
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const dayStart = (ymd: string) => (YMD.test(ymd) ? new Date(`${ymd}T00:00:00.000${KST}`).toISOString() : undefined);
const dayEnd = (ymd: string) => (YMD.test(ymd) ? new Date(`${ymd}T23:59:59.999${KST}`).toISOString() : undefined);

/** 화면 검색 조건 → BE 쿼리. 결과가 없을 조합(공개+작성중만 등)이면 null. */
export const toFundingListQuery = (
  f: FundingListFilter,
  sort: FundingListSort | null,
  page: number,
): Record<string, unknown> | null => {
  let statuses: ReadonlyArray<FundingStatusEnum> = f.statuses;
  if (f.visibility !== 'ALL') {
    const allowed = FUNDING_STATUS_VALUES.filter((s) => (f.visibility === 'PRIVATE' ? s === 'DRAFT' : s !== 'DRAFT'));
    statuses = statuses.length > 0 ? statuses.filter((s) => allowed.includes(s)) : allowed;
    if (statuses.length === 0) return null;
  }
  const q: Record<string, unknown> = {
    page: Math.max(1, page),
    size: PAGE_SIZE,
    sort: sort ? `${sort.key},${sort.direction}` : 'createdAt,desc',
  };
  if (statuses.length > 0) q.status = [...statuses];
  if (f.keyword.trim()) q.keyword = f.keyword.trim();
  if (f.artistId) q.artistId = f.artistId;
  if (f.category) q.category = f.category;
  const dates: Array<[string, string | undefined]> = [
    ['periodFrom', dayStart(f.periodFrom)],
    ['periodTo', dayEnd(f.periodTo)],
    ['launchedFrom', dayStart(f.launchedFrom)],
    ['launchedTo', dayEnd(f.launchedTo)],
    ['closedFrom', dayStart(f.closedFrom)],
    ['closedTo', dayEnd(f.closedTo)],
  ];
  for (const [k, v] of dates) if (v) q[k] = v;
  return q;
};

const toRow = (r: ListItem): FundingRow => ({
  campaignId: r.campaignId,
  title: r.title,
  status: r.status,
  artistId: r.artistId ?? null,
  artistName: r.artistName ?? null,
  category: r.category ?? null,
  goalAmount: r.goalAmount ?? null,
  currentAmount: r.currentAmount ?? null,
  achievementRate: r.achievementRate ?? null,
  pledgeCount: r.currentPledgeCount ?? null,
  rewardCount: r.rewardCount ?? null,
  startAt: r.startAt ?? null,
  endAt: r.endAt ?? null,
  launchedAt: r.launchedAt ?? null,
  closedAt: r.closedAt ?? null,
  createdAt: r.createdAt ?? null,
});

export const useFundingRows = (filter: FundingListFilter, sort: FundingListSort | null, page: number) => {
  const query = toFundingListQuery(filter, sort, page);
  const list = useQuery({
    queryKey: ['admin', 'funding-campaigns', 'list', query] as const,
    enabled: query !== null,
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/funding-campaigns', {
        params: { query: query as never },
      });
      return { items: ((data as { items?: ListItem[] }).items ?? []).map(toRow), total: meta?.pagination?.totalItems ?? 0 };
    },
  });
  if (query === null) return { rows: [] as FundingRow[], total: 0, isPending: false, isError: false, refetch: list.refetch };
  return {
    rows: list.data?.items ?? [],
    total: list.data?.total ?? 0,
    isPending: list.isPending,
    isError: list.isError,
    refetch: list.refetch,
  };
};
